
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/db';
import BillModal from '../components/BillModal';
import LoadingOverlay from '../components/LoadingOverlay';
import './SalesPrevious.css';

export default function SalesPrevious() {
    const { warehouseId } = useAuth();
    const [loading, setLoading] = useState(true);
    const [groups, setGroups] = useState([]);

    // Bill Modal State
    const [billData, setBillData] = useState(null);
    const [isBillOpen, setIsBillOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        if (warehouseId) {
            loadTransactions();
        }
        // eslint-disable-next-line
    }, [warehouseId]);

    async function loadTransactions(retryCount = 0) {
        setLoading(true);
        try {
            const docs = await api.getSalesTransactions(warehouseId);

            // Group by sale_transaction_code
            const grouped = {};

            docs.forEach(doc => {
                const code = doc.sale_transaction_code;
                if (!grouped[code]) {
                    // Initialize group
                    grouped[code] = {
                        code,
                        date: new Date(doc.$createdAt).toLocaleDateString('ar-EG'), // Display Date
                        fullDate: new Date(doc.$createdAt), // Sort Date
                        createdAt: doc.$createdAt, // Time
                        type: code.startsWith('SW') ? 'وارد' : 'بيع',
                        receiver: doc.sale_notes, // "Receiver" for sale, "Source" for incoming
                        user: doc.user_name,
                        totalAmount: 0,
                        items: []
                    };
                }

                // Aggregate Cost
                const cost = Number(doc.total_cost) || 0;
                grouped[code].totalAmount += cost;

                // Add Item
                grouped[code].items.push({
                    id: doc.$id,
                    name: doc.item_name,
                    qty: doc.quantity,
                    price: cost, // This is the total price for this row
                    unitPrice: doc.unit_price // New field
                });
            });

            // Convert to array and sort by date desc (newest first)
            const sorted = Object.values(grouped).sort((a, b) => b.fullDate - a.fullDate);
            setGroups(sorted);
            setLoading(false);

        } catch (error) {
            console.error("Failed to load previous transactions, retrying...", error);
            // Retry after a delay (keep loading, don't show error)
            const delay = Math.min(3000 * (retryCount + 1), 10000); // 3s, 6s, 9s, max 10s
            setTimeout(() => loadTransactions(retryCount + 1), delay);
        }
    }

    function handleViewBill(group) {
        if (group.type !== 'بيع') return;

        const data = {
            date: group.date,
            receiptCode: group.code,
            receiver: group.receiver,
            items: group.items,
            total: Math.round(group.totalAmount).toLocaleString('en-US')
        };
        setBillData(data);
        setIsBillOpen(true);
    }

    function formatTime(timestamp) {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        let hours = date.getHours();
        const minutes = date.getMinutes();
        const period = hours >= 12 ? 'م' : 'ص';

        hours = hours % 12;
        hours = hours ? hours : 12;

        const minutesStr = minutes < 10 ? '0' + minutes : minutes;
        return `${hours}:${minutesStr} ${period}`;
    }

    return (
        <div className="previous-root" dir="rtl">
            <LoadingOverlay isLoading={loading} />

            <div><Link to="/sales-home"><button className="back-btn">الرجوع</button></Link></div>

            <div className="container">
                <h1>معاملات المبيعات السابقة</h1>

                <div className="search-box">
                    <label>بحث:</label>
                    <input
                        type="text"
                        placeholder="ابحث باسم العميل أو رقم الفاتورة..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>

                {groups.filter(g =>
                    g.receiver.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    g.code.toLowerCase().includes(searchQuery.toLowerCase())
                ).length === 0 && !loading ? (
                    <p style={{ textAlign: 'center', padding: 20, color: '#888' }}>{searchQuery ? 'لم يتم العثور على نتائج' : 'لا توجد معاملات سابقة'}</p>
                ) : (
                    <div id="transactions-list">
                        {groups.filter(g =>
                            g.receiver.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            g.code.toLowerCase().includes(searchQuery.toLowerCase())
                        ).map((group) => (
                            <div className="tx-card" key={group.code} style={{
                                borderLeft: group.type === 'بيع' ? '5px solid #2ecc71' : '5px solid #3498db'
                            }}>
                                <div style={{ position: 'relative' }}>

                                    {/* Action Buttons Top Left */}
                                    {group.type === 'بيع' && (
                                        <button
                                            className="edit-btn-top-left"
                                            onClick={() => handleViewBill(group)}
                                            style={{ background: '#007db8ff' }} // Orange for "View Bill"
                                        >
                                            عرض الفاتورة
                                        </button>
                                    )}

                                    <div className="tx-meta" style={{ marginTop: 25 }}>
                                        <div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                <strong>رقم المعاملة: {group.code}</strong>
                                            </div>
                                            <div><strong>التاريخ:</strong> {group.date}</div>
                                            <div><strong>الوقت:</strong> {formatTime(group.createdAt)}</div>

                                            <div>
                                                <strong>النوع:</strong> {group.type}
                                            </div>

                                            <div>
                                                <strong>{group.type === 'بيع' ? 'المستلم:' : 'الجهة/المصدر:'}</strong> {group.receiver}
                                            </div>

                                            <div>
                                                <strong>المستخدم:</strong> {group.user}
                                            </div>

                                            {/* Show Total Amount for Sales */}
                                            {group.type === 'بيع' && (
                                                <div style={{ marginTop: 5, color: '#27ae60', fontWeight: 'bold' }}>
                                                    <strong>الإجمالي:</strong> {Math.round(group.totalAmount).toLocaleString('en-US')}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="tx-items">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>الصنف</th>
                                                <th>الكمية</th>
                                                {group.type === 'بيع' && <th>السعر (إجمالي)</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {group.items.map((it, idx) => (
                                                <tr key={idx}>
                                                    <td>{it.name}</td>
                                                    <td>{it.qty}</td>
                                                    {group.type === 'بيع' && (
                                                        <td>
                                                            {Math.round(it.price).toLocaleString('en-US')}
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <BillModal
                isOpen={isBillOpen}
                onClose={() => setIsBillOpen(false)}
                data={billData}
            />
        </div>
    );
}
